import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { agrogestao } from "@/api/agrogestaoClient";

const PropriedadeContext = createContext(null);

export function PropriedadeProvider({ children }) {
  const [propriedades, setPropriedades] = useState([]);
  const [propriedadeSelecionada, setPropriedadeSelecionada] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [permissoes, setPermissoes] = useState(null);

  // Carregar propriedades do usuário via backend function
  const carregarPropriedades = useCallback(async () => {
    const u = await agrogestao.auth.me();
    if (!u) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await agrogestao.functions.invoke('listarPropriedadesUsuario');
      if (response.data?.success) {
        setPropriedades(response.data.propriedades);
        const savedId = localStorage.getItem("propriedade_selecionada");
        const propEncontrada = response.data.propriedades.find(p => p.id === savedId);
        if (propEncontrada) {
          setPropriedadeSelecionada(propEncontrada);
        } else if (response.data.propriedades.length > 0) {
          setPropriedadeSelecionada(response.data.propriedades[0]);
          localStorage.setItem("propriedade_selecionada", response.data.propriedades[0].id);
        }
        return;
      }
      // Fallback: consulta direta com RLS por created_by
      const u = await agrogestao.auth.me();
      if (u?.email) {
        const props = await agrogestao.entities.Propriedade.filter({ created_by: u.email });
        setPropriedades(props);
        const savedId = localStorage.getItem("propriedade_selecionada");
        const propEncontrada = props.find(p => p.id === savedId);
        if (propEncontrada) {
          setPropriedadeSelecionada(propEncontrada);
        } else if (props.length > 0) {
          setPropriedadeSelecionada(props[0]);
          localStorage.setItem("propriedade_selecionada", props[0].id);
        }
      }
    } catch (err) {
      // Fallback em caso de erro (ex.: CORS na Edge Function)
      try {
        const u = await agrogestao.auth.me();
        if (u?.email) {
          const props = await agrogestao.entities.Propriedade.filter({ created_by: u.email });
          setPropriedades(props);
          const savedId = localStorage.getItem("propriedade_selecionada");
          const propEncontrada = props.find(p => p.id === savedId);
          if (propEncontrada) {
            setPropriedadeSelecionada(propEncontrada);
          } else if (props.length > 0) {
            setPropriedadeSelecionada(props[0]);
            localStorage.setItem("propriedade_selecionada", props[0].id);
          }
        }
      } catch (e2) {
        console.error("Erro ao carregar propriedades:", e2);
        setError("Erro ao carregar propriedades");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Validar acesso a uma propriedade específica
  const validarAcesso = useCallback(async (propriedadeId, acao = 'leitura') => {
    if (!propriedadeId) return { success: false, error: "ID da propriedade não fornecido" };
    
    const u = await agrogestao.auth.me();
    if (!u) return { success: false, error: "Usuário não autenticado" };

    try {
      const response = await agrogestao.functions.invoke('validarAcessoPropriedade', {
        propriedade_id: propriedadeId,
        acao
      });
      
      if (response.data?.success) {
        setPermissoes(response.data.permissoes);
        return { success: true, permissoes: response.data.permissoes };
      } else {
        // Fallback: permitir se usuário for criador
        const u = await agrogestao.auth.me();
        if (u?.email) {
          const props = await agrogestao.entities.Propriedade.filter({ id: propriedadeId });
          const p = props?.[0];
          if (p?.created_by === u.email) {
            return { success: true, permissoes: { owner: true } };
          }
        }
        await agrogestao.access.logAudit({ propriedade_id: propriedadeId, acao, table: 'propriedades', status: 'denied' });
        return { success: false, error: response.data?.error };
      }
    } catch (err) {
      // Fallback em caso de erro (ex.: CORS)
      try {
        const u = await agrogestao.auth.me();
        if (u?.email) {
          const props = await agrogestao.entities.Propriedade.filter({ id: propriedadeId });
          const p = props?.[0];
          if (p?.created_by === u.email) {
            return { success: true, permissoes: { owner: true } };
          }
        }
      } catch {}
      await agrogestao.access.logAudit({ propriedade_id: propriedadeId, acao, table: 'propriedades', status: 'denied' });
      console.error("Erro ao validar acesso:", err);
      return { success: false, error: "Erro ao validar acesso" };
    }
  }, []);

  // Selecionar propriedade com validação
  const selecionarPropriedade = useCallback(async (propriedade) => {
    const resultado = await validarAcesso(propriedade.id, 'selecao');
    
    if (resultado.success) {
      setPropriedadeSelecionada(propriedade);
      setPermissoes(resultado.permissoes);
      localStorage.setItem("propriedade_selecionada", propriedade.id);
      return { success: true };
    } else {
      return { success: false, error: resultado.error };
    }
  }, [validarAcesso]);

  // Carregar ao montar
  useEffect(() => {
    carregarPropriedades();
  }, [carregarPropriedades]);

  const value = {
    propriedades,
    propriedadeSelecionada,
    propriedadeId: propriedadeSelecionada?.id,
    loading,
    error,
    permissoes,
    carregarPropriedades,
    selecionarPropriedade,
    validarAcesso
  };

  return (
    <PropriedadeContext.Provider value={value}>
      {children}
    </PropriedadeContext.Provider>
  );
}

export function usePropriedade() {
  const context = useContext(PropriedadeContext);
  if (!context) {
    throw new Error("usePropriedade deve ser usado dentro de PropriedadeProvider");
  }
  return context;
}

export default PropriedadeContext;
